/**
 * End-to-end encryption primitives (Web Crypto): ECDH P-256 for key agreement
 * + AES-GCM for message content. The server only ever relays public keys and
 * stores ciphertext — it cannot read messages.
 *
 * Scope/limitations (honest): this secures a *live 1:1* session — the guest and
 * one agent exchange public keys while both are connected, then messages between
 * them are encrypted. True asynchronous E2E (encrypting to an offline party)
 * needs a prekey/X3DH scheme, which is out of scope here. When E2E is on, the
 * AI assistant cannot read the room (by design).
 */

const subtle = (): SubtleCrypto => globalThis.crypto.subtle

function b64encode(buf: ArrayBuffer | Uint8Array): string {
  const bytes = buf instanceof Uint8Array ? buf : new Uint8Array(buf)
  let s = ''
  for (const b of bytes) s += String.fromCharCode(b)
  return btoa(s)
}
function b64decode(s: string): Uint8Array<ArrayBuffer> {
  const bin = atob(s)
  const buf = new ArrayBuffer(bin.length)
  const out = new Uint8Array(buf)
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i)
  return out
}

export interface KeyPair { publicKey: CryptoKey; privateKey: CryptoKey }

export async function generateKeyPair(): Promise<KeyPair> {
  const kp = await subtle().generateKey({ name: 'ECDH', namedCurve: 'P-256' }, true, ['deriveKey', 'deriveBits'])
  return { publicKey: kp.publicKey, privateKey: kp.privateKey }
}

/** Export a public key to a compact base64 string (raw, 65 bytes for P-256). */
export async function exportPublicKey(key: CryptoKey): Promise<string> {
  return b64encode(await subtle().exportKey('raw', key))
}

async function importPeerPublicKey(b64: string): Promise<CryptoKey> {
  return subtle().importKey('raw', b64decode(b64), { name: 'ECDH', namedCurve: 'P-256' }, false, [])
}

/** Derive the shared AES-GCM key from our private key + the peer's public key. */
export async function deriveSharedKey(privateKey: CryptoKey, peerPublicKeyB64: string): Promise<CryptoKey> {
  const peer = await importPeerPublicKey(peerPublicKeyB64)
  return subtle().deriveKey(
    { name: 'ECDH', public: peer },
    privateKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  )
}

export interface Ciphertext { ct: string; iv: string }

export async function encrypt(key: CryptoKey, plaintext: string): Promise<Ciphertext> {
  const iv = globalThis.crypto.getRandomValues(new Uint8Array(12))
  const data = new TextEncoder().encode(plaintext)
  const ct = await subtle().encrypt({ name: 'AES-GCM', iv }, key, data)
  return { ct: b64encode(ct), iv: b64encode(iv) }
}

export async function decrypt(key: CryptoKey, ct: string, iv: string): Promise<string> {
  const plain = await subtle().decrypt({ name: 'AES-GCM', iv: b64decode(iv) }, key, b64decode(ct))
  return new TextDecoder().decode(plain)
}

/** Persist/restore our keypair across reloads (so prior ciphertext stays readable). */
export async function loadOrCreateKeyPair(storageKey: string): Promise<KeyPair> {
  try {
    const raw = globalThis.localStorage?.getItem(storageKey)
    if (raw) {
      const { pub, priv } = JSON.parse(raw) as { pub: JsonWebKey; priv: JsonWebKey }
      const publicKey = await subtle().importKey('jwk', pub, { name: 'ECDH', namedCurve: 'P-256' }, true, [])
      const privateKey = await subtle().importKey('jwk', priv, { name: 'ECDH', namedCurve: 'P-256' }, true, ['deriveKey', 'deriveBits'])
      return { publicKey, privateKey }
    }
  } catch { /* fall through to fresh keys */ }
  const kp = await generateKeyPair()
  try {
    const pub = await subtle().exportKey('jwk', kp.publicKey)
    const priv = await subtle().exportKey('jwk', kp.privateKey)
    globalThis.localStorage?.setItem(storageKey, JSON.stringify({ pub, priv }))
  } catch { /* non-persistent environment is fine */ }
  return kp
}

// ── X3DH async E2E ────────────────────────────────────────────────────────────
// Extended Triple Diffie-Hellman (X3DH) allows encrypting to an *offline* peer
// using their published prekey bundle. This enables asynchronous E2E: the sender
// can encrypt before the recipient connects.
//
// Key roles:
//   IK  = long-term identity key (ECDH P-256, persistent in localStorage)
//   SPK = signed prekey (ECDH P-256, rotated periodically, server-stored)
//   OPK = one-time prekey (ECDH P-256, single-use pool, server-stored)
//   EK  = ephemeral key (ECDH P-256, generated per-message, discarded after)
//
// X3DH shared secret = KDF(DH(IK_s, SPK_r) || DH(EK, IK_r) || DH(EK, SPK_r) || DH(EK, OPK_r))
// Where _s = sender, _r = recipient.

/** Sign a prekey public key bytes using ECDSA P-256 SHA-256.
 *  The signingKey must be an ECDSA P-256 private key (not ECDH).
 *  In the full X3DH setup the identity key pair contains both an ECDH key
 *  (for DH) and an ECDSA key (for signing). We keep them separate here. */
export async function signPrekey(signingPrivateKey: CryptoKey, spkPublicKey: CryptoKey): Promise<string> {
  const spkRaw = await subtle().exportKey('raw', spkPublicKey)
  const sig = await subtle().sign({ name: 'ECDSA', hash: 'SHA-256' }, signingPrivateKey, spkRaw)
  return b64encode(sig)
}

/** Verify an SPK signature. verifyPublicKey must be an ECDSA P-256 public key. */
export async function verifyPrekeySignature(verifyPublicKeyB64: string, spkPublicKeyB64: string, signatureB64: string): Promise<boolean> {
  try {
    const verKey = await subtle().importKey('raw', b64decode(verifyPublicKeyB64), { name: 'ECDSA', namedCurve: 'P-256' }, false, ['verify'])
    return await subtle().verify({ name: 'ECDSA', hash: 'SHA-256' }, verKey, b64decode(signatureB64), b64decode(spkPublicKeyB64))
  } catch { return false }
}

/** A full identity keypair for X3DH: ECDH key for DH computations + ECDSA key
 *  for signing prekeys. The two key objects share the same P-256 curve but have
 *  different usages, so Web Crypto treats them separately. */
export interface IdentityKeyPair {
  ecdhKP:   KeyPair   // for DH in X3DH
  ecdsaKP:  { publicKey: CryptoKey; privateKey: CryptoKey }  // for signing SPKs
  /** The ECDH public key exported as base64 — used as the X3DH identity key. */
  publicKeyB64: string
  /** The ECDSA public key exported as base64 — used for SPK signature verification. */
  sigPublicKeyB64: string
}

/** Generate a full X3DH identity keypair (ECDH + ECDSA on the same P-256 curve). */
export async function generateIdentityKeyPair(): Promise<IdentityKeyPair> {
  const ecdhKP  = await generateKeyPair()
  const ecdsaKP = await subtle().generateKey({ name: 'ECDSA', namedCurve: 'P-256' }, true, ['sign', 'verify'])
  return {
    ecdhKP,
    ecdsaKP: { publicKey: ecdsaKP.publicKey, privateKey: ecdsaKP.privateKey },
    publicKeyB64:    await exportPublicKey(ecdhKP.publicKey),
    sigPublicKeyB64: await exportPublicKey(ecdsaKP.publicKey),
  }
}

/** Load or generate an identity keypair, persisting both components. */
export async function loadOrCreateIdentityKeyPair(storageKey: string): Promise<IdentityKeyPair> {
  try {
    const raw = globalThis.localStorage?.getItem(`${storageKey}-identity`)
    if (raw) {
      const d = JSON.parse(raw) as { ecdhPub: JsonWebKey; ecdhPriv: JsonWebKey; ecdsaPub: JsonWebKey; ecdsaPriv: JsonWebKey }
      const ecdhPub   = await subtle().importKey('jwk', d.ecdhPub, { name: 'ECDH', namedCurve: 'P-256' }, true, [])
      const ecdhPriv  = await subtle().importKey('jwk', d.ecdhPriv, { name: 'ECDH', namedCurve: 'P-256' }, true, ['deriveKey', 'deriveBits'])
      const ecdsaPub  = await subtle().importKey('jwk', d.ecdsaPub, { name: 'ECDSA', namedCurve: 'P-256' }, true, ['verify'])
      const ecdsaPriv = await subtle().importKey('jwk', d.ecdsaPriv, { name: 'ECDSA', namedCurve: 'P-256' }, true, ['sign'])
      return {
        ecdhKP: { publicKey: ecdhPub, privateKey: ecdhPriv },
        ecdsaKP: { publicKey: ecdsaPub, privateKey: ecdsaPriv },
        publicKeyB64:    await exportPublicKey(ecdhPub),
        sigPublicKeyB64: await exportPublicKey(ecdsaPub),
      }
    }
  } catch { /* generate fresh */ }
  const ikp = await generateIdentityKeyPair()
  try {
    const ecdhPub   = await subtle().exportKey('jwk', ikp.ecdhKP.publicKey)
    const ecdhPriv  = await subtle().exportKey('jwk', ikp.ecdhKP.privateKey)
    const ecdsaPub  = await subtle().exportKey('jwk', ikp.ecdsaKP.publicKey)
    const ecdsaPriv = await subtle().exportKey('jwk', ikp.ecdsaKP.privateKey)
    globalThis.localStorage?.setItem(`${storageKey}-identity`, JSON.stringify({ ecdhPub, ecdhPriv, ecdsaPub, ecdsaPriv }))
  } catch { /* non-persistent ok */ }
  return ikp
}

export interface X3DHBundle {
  identityKey:    string  // base64 raw P-256 public key
  signedPrekey:   string  // base64 raw P-256 public key
  signedPrekeyId: string  // opaque ID for key rotation tracking
  signature:      string  // base64 ECDSA signature of SPK by IK
  oneTimePrekey?: string  // base64 raw P-256 public key (optional)
}

/** X3DH sender side: derive a shared key from the recipient's prekey bundle.
 *  Returns the shared AES-GCM key and the ephemeral public key to transmit. */
export async function x3dhSend(
  senderIK: KeyPair,
  recipientBundle: X3DHBundle,
): Promise<{ sharedKey: CryptoKey; ephemeralPublicKey: string }> {
  const ek = await generateKeyPair()
  const epkB64 = await exportPublicKey(ek.publicKey)

  // Import recipient keys for DH.
  const ik_r = await importPeerPublicKey(recipientBundle.identityKey)
  const spk_r = await importPeerPublicKey(recipientBundle.signedPrekey)
  const opk_r = recipientBundle.oneTimePrekey ? await importPeerPublicKey(recipientBundle.oneTimePrekey) : null

  // Four DH computations per spec (three if no OPK).
  const dh1 = await rawDH(senderIK.privateKey, spk_r)   // DH(IK_s, SPK_r)
  const dh2 = await rawDH(ek.privateKey, ik_r)           // DH(EK, IK_r)
  const dh3 = await rawDH(ek.privateKey, spk_r)          // DH(EK, SPK_r)
  const dh4 = opk_r ? await rawDH(ek.privateKey, opk_r) : null  // DH(EK, OPK_r)

  const ikm = concatBuffers(dh1, dh2, dh3, ...(dh4 ? [dh4] : []))
  const sharedKey = await hkdfDeriveKey(ikm)

  return { sharedKey, ephemeralPublicKey: epkB64 }
}

/** X3DH recipient side: rederive the shared key from an init message.
 *  Returns the shared AES-GCM key. */
export async function x3dhReceive(
  recipientIK: KeyPair,
  recipientSPK: KeyPair,
  senderIKb64: string,
  ephemeralKeyB64: string,
  recipientOPK?: KeyPair,
): Promise<CryptoKey> {
  const ik_s = await importPeerPublicKey(senderIKb64)
  const ek_s = await importPeerPublicKey(ephemeralKeyB64)

  const dh1 = await rawDH(recipientSPK.privateKey, ik_s)       // DH(SPK_r, IK_s)
  const dh2 = await rawDH(recipientIK.privateKey, ek_s)        // DH(IK_r, EK)
  const dh3 = await rawDH(recipientSPK.privateKey, ek_s)       // DH(SPK_r, EK)
  const dh4 = recipientOPK ? await rawDH(recipientOPK.privateKey, ek_s) : null

  const ikm = concatBuffers(dh1, dh2, dh3, ...(dh4 ? [dh4] : []))
  return hkdfDeriveKey(ikm)
}

async function rawDH(privateKey: CryptoKey, publicKey: CryptoKey): Promise<ArrayBuffer> {
  return subtle().deriveBits({ name: 'ECDH', public: publicKey }, privateKey, 256)
}

function concatBuffers(...bufs: ArrayBuffer[]): ArrayBuffer {
  const total = bufs.reduce((n, b) => n + b.byteLength, 0)
  const out = new Uint8Array(total)
  let offset = 0
  for (const b of bufs) { out.set(new Uint8Array(b), offset); offset += b.byteLength }
  return out.buffer
}

async function hkdfDeriveKey(ikm: ArrayBuffer): Promise<CryptoKey> {
  const ikmKey = await subtle().importKey('raw', ikm, 'HKDF', false, ['deriveKey'])
  return subtle().deriveKey(
    { name: 'HKDF', hash: 'SHA-256', salt: new Uint8Array(32), info: new TextEncoder().encode('ObjectChat X3DH v1') },
    ikmKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  )
}
